import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Alert, Avatar, Button, Card, Empty, Progress, Segmented, Skeleton, Table, Tag, Tooltip } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  InboxOutlined,
  MessageOutlined,
  MinusOutlined,
  ReloadOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { liveChatApi, ForbiddenError } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { SourceTag, OPERATOR_STATUS } from '../../components/live-chat/liveChatMeta';

const PRESETS = [
  { label: 'امروز', value: 1 },
  { label: '۷ روز اخیر', value: 7 },
  { label: '۳۰ روز اخیر', value: 30 },
  { label: '۹۰ روز اخیر', value: 90 },
];

const WEEKDAYS_FA = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
const PALETTE = ['#0d9488', '#2563eb', '#d97706', '#7c3aed', '#dc2626', '#16a34a', '#0891b2', '#db2777'];

const fmtCount = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const fmtPercent = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`);
const fmtDuration = (sec) => {
  if (sec == null) return '—';
  const s = Math.round(Number(sec));
  if (s < 60) return `${s} ثانیه`;
  if (s < 3600) return `${Math.floor(s / 60)} دقیقه`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h < 24) return m ? `${h} ساعت و ${m} دقیقه` : `${h} ساعت`;
  return `${Math.floor(h / 24)} روز و ${h % 24} ساعت`;
};
const shortDay = (d) => moment(d, 'YYYY-MM-DD').format('jMM/jDD');
const waitingLabel = (iso) => {
  if (!iso) return '—';
  const sec = Math.max(0, moment().diff(moment(iso), 'seconds'));
  return fmtDuration(sec);
};

/** اختلاف نسبی با دوره قبل؛ good = جهت مطلوب */
const Delta = ({ cur, prev, good = 'up' }) => {
  if (cur == null || prev == null || prev === 0) {
    return <span className="text-xs text-gray-400 custom-font flex items-center gap-1"><MinusOutlined /> بدون مقایسه</span>;
  }
  const pct = ((cur - prev) / prev) * 100;
  if (Math.abs(pct) < 0.5) {
    return <span className="text-xs text-gray-400 custom-font flex items-center gap-1"><MinusOutlined /> بدون تغییر</span>;
  }
  const up = pct > 0;
  const isGood = good === 'neutral' ? null : (good === 'up') === up;
  const color = isGood == null ? 'text-gray-500' : isGood ? 'text-emerald-600' : 'text-red-600';
  return (
    <span className={`text-xs custom-font flex items-center gap-1 ${color}`}>
      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {Math.abs(pct).toLocaleString('en-US', { maximumFractionDigits: 0 })}٪ نسبت به دوره قبل
    </span>
  );
};

const KpiCard = ({ label, value, icon, color, hint, footer, loading }) => (
  <Card className="shadow-sm border-0 h-full" styles={{ body: { padding: 16 } }}>
    {loading ? (
      <Skeleton active paragraph={{ rows: 2 }} title={false} />
    ) : (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Tooltip title={hint}>
            <div className="text-xs text-gray-500 dark:text-gray-400 custom-font mb-1">{label}</div>
          </Tooltip>
          <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 custom-font leading-tight">{value}</div>
          <div className="mt-1">{footer}</div>
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
          style={{ background: `${color}1a`, color }}
        >
          {icon}
        </div>
      </div>
    )}
  </Card>
);

const Section = ({ title, extra, children, className = '' }) => (
  <Card
    className={`shadow-sm border-0 ${className}`}
    styles={{ body: { padding: 16 } }}
    title={<span className="custom-font text-sm">{title}</span>}
    extra={extra}
  >
    {children}
  </Card>
);

const ChartEmpty = () => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="custom-font">داده‌ای در این بازه نیست</span>} />;

const chartTooltipStyle = { fontFamily: 'CustomFont', fontSize: 12, borderRadius: 8 };

const LiveChatDashboard = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async (d, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await liveChatApi.getDashboard({ days: d });
      setData(res?.data ?? null);
    } catch (e) {
      setError(e instanceof ForbiddenError ? 'شما به داشبورد گفتگوی آنلاین دسترسی ندارید.' : 'بارگذاری داشبورد ناموفق بود.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  // به‌روزرسانی خودکار بخش زنده هر ۶۰ ثانیه
  useEffect(() => {
    const t = setInterval(() => load(days, true), 60000);
    return () => clearInterval(t);
  }, [days, load]);

  const cur = data?.kpis?.current;
  const prev = data?.kpis?.previous;
  const live = data?.kpis?.live;

  const daily = useMemo(() => (data?.daily ?? []).map((d) => ({ ...d, label: shortDay(d.date) })), [data]);
  const hours = useMemo(() => (data?.by_hour ?? []).map((h) => ({ ...h, label: String(h.hour).padStart(2, '0') })), [data]);
  const weekdays = useMemo(() => (data?.by_weekday ?? []).map((w) => ({ name: WEEKDAYS_FA[w.index], count: w.count })), [data]);
  const peakHour = useMemo(() => {
    const best = [...hours].sort((a, b) => b.count - a.count)[0];
    return best && best.count > 0 ? best : null;
  }, [hours]);
  const hasAny = (cur?.conversations ?? 0) > 0 || (live?.open ?? 0) > 0;

  const sourceData = (data?.by_source ?? []).map((s, i) => ({ ...s, fill: s.color || PALETTE[i % PALETTE.length] }));
  const deptTotal = (data?.by_department ?? []).reduce((a, d) => a + d.count, 0);

  const operatorColumns = [
    {
      title: 'اپراتور',
      dataIndex: 'name',
      render: (name, r) => (
        <div className="flex items-center gap-2">
          <Avatar size={28} src={r.image ? getImagePath(r.image) : undefined} icon={<UserOutlined />} />
          <span className="custom-font">{name}</span>
          <Tag color={OPERATOR_STATUS[r.status]?.color} className="custom-font" style={{ marginInlineEnd: 0 }}>
            {OPERATOR_STATUS[r.status]?.label ?? r.status}
          </Tag>
        </div>
      ),
    },
    { title: 'گفتگوی دریافتی', dataIndex: 'handled', align: 'center', sorter: (a, b) => a.handled - b.handled, defaultSortOrder: 'descend', render: fmtCount },
    { title: 'بسته‌شده', dataIndex: 'closed', align: 'center', sorter: (a, b) => a.closed - b.closed, render: fmtCount },
    { title: 'باز اکنون', dataIndex: 'open', align: 'center', sorter: (a, b) => a.open - b.open, render: fmtCount },
    { title: 'پاسخ‌ها', dataIndex: 'replies', align: 'center', sorter: (a, b) => a.replies - b.replies, render: fmtCount },
    {
      title: 'میانگین اولین پاسخ',
      dataIndex: 'avg_first_response_sec',
      align: 'center',
      sorter: (a, b) => (a.avg_first_response_sec ?? Infinity) - (b.avg_first_response_sec ?? Infinity),
      render: (v) => <span className="custom-font">{fmtDuration(v)}</span>,
    },
  ];

  const attentionColumns = [
    { title: 'مشتری', dataIndex: 'contact', render: (v) => <span className="custom-font">{v}</span> },
    { title: 'منبع', dataIndex: 'source', render: (s) => <SourceTag source={s} /> },
    { title: 'بخش', dataIndex: 'department', render: (v) => <span className="custom-font">{v || '—'}</span> },
    {
      title: 'وضعیت',
      render: (_, r) =>
        r.operator ? (
          <Tag color="blue" className="custom-font">{r.operator}</Tag>
        ) : (
          <Tag color="red" className="custom-font">بدون اپراتور</Tag>
        ),
    },
    {
      title: 'خوانده‌نشده',
      dataIndex: 'unread_count',
      align: 'center',
      render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : '—'),
    },
    { title: 'در انتظار', dataIndex: 'waiting_since', render: (v) => <span className="custom-font">{waitingLabel(v)}</span> },
    {
      title: '',
      render: (_, r) => (
        <Button size="small" type="link" className="custom-font" onClick={() => navigate(`/live-chat?conversation=${r.id}`)}>
          باز کردن
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <DashboardOutlined className="text-xl text-brand-primary" />
            <div>
              <div className="text-lg font-bold text-gray-800 dark:text-gray-100 custom-font">داشبورد گفتگوی آنلاین</div>
              <div className="text-xs text-gray-500 custom-font">شاخص‌های کلیدی، روند گفتگوها و عملکرد اپراتورها</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Segmented options={PRESETS} value={days} onChange={setDays} className="custom-font" />
            <Tooltip title="بازخوانی">
              <Button icon={<ReloadOutlined />} onClick={() => load(days)} loading={loading} />
            </Tooltip>
          </div>
        </div>

        {error && <Alert type="error" showIcon message={<span className="custom-font">{error}</span>} />}

        {/* وضعیت زنده */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiCard loading={loading && !data} label="گفتگوهای باز" value={fmtCount(live?.open)} icon={<MessageOutlined />} color="#2563eb"
            hint="گفتگوهای باز در همین لحظه" footer={<span className="text-xs text-gray-400 custom-font">اکنون</span>} />
          <KpiCard loading={loading && !data} label="بدون اپراتور" value={fmtCount(live?.unassigned)} icon={<InboxOutlined />}
            color={live?.unassigned > 0 ? '#dc2626' : '#16a34a'} hint="گفتگوهای بازی که هنوز به اپراتوری ارجاع نشده‌اند"
            footer={<span className="text-xs text-gray-400 custom-font">نیازمند ارجاع</span>} />
          <KpiCard loading={loading && !data} label="منتظر انتخاب بخش" value={fmtCount(live?.awaiting_department)} icon={<ClockCircleOutlined />} color="#d97706"
            hint="مشتریانی که هنوز بخش را انتخاب نکرده‌اند" footer={<span className="text-xs text-gray-400 custom-font">در مسیر شروع گفتگو</span>} />
          <KpiCard loading={loading && !data} label="گفتگوی خوانده‌نشده" value={fmtCount(live?.unread_chats)} icon={<WarningOutlined />} color="#db2777"
            hint="گفتگوهای باز دارای پیام خوانده‌نشده" footer={<span className="text-xs text-gray-400 custom-font">پیام بدون بازدید</span>} />
          <KpiCard loading={loading && !data} label="اپراتور آنلاین" value={`${fmtCount(live?.operators_online)} / ${fmtCount(live?.operators_total)}`} icon={<TeamOutlined />} color="#0d9488"
            hint="اپراتورهای آنلاین از کل اپراتورهای فعال" footer={<span className="text-xs text-gray-400 custom-font">فعال اکنون</span>} />
          <KpiCard loading={loading && !data} label="ساعت اوج پیام" value={peakHour ? `${peakHour.label}:00` : '—'} icon={<ThunderboltOutlined />} color="#7c3aed"
            hint="پرترافیک‌ترین ساعت پیام‌های ورودی در بازه انتخابی"
            footer={<span className="text-xs text-gray-400 custom-font">{peakHour ? `${fmtCount(peakHour.count)} پیام` : 'بدون داده'}</span>} />
        </div>

        {/* شاخص‌های بازه */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          <KpiCard loading={loading && !data} label="گفتگوی جدید" value={fmtCount(cur?.conversations)} icon={<CommentOutlined />} color="#2563eb"
            hint="گفتگوهای ایجادشده در بازه" footer={<Delta cur={cur?.conversations} prev={prev?.conversations} good="neutral" />} />
          <KpiCard loading={loading && !data} label="مشتریان یکتا" value={fmtCount(cur?.unique_contacts)} icon={<UserOutlined />} color="#0891b2"
            hint="تعداد مشتریان متفاوتی که گفتگو آغاز کرده‌اند" footer={<Delta cur={cur?.unique_contacts} prev={prev?.unique_contacts} good="neutral" />} />
          <KpiCard loading={loading && !data} label="میانگین اولین پاسخ" value={fmtDuration(cur?.avg_first_response_sec)} icon={<FieldTimeOutlined />} color="#d97706"
            hint="میانگین زمان از شروع گفتگو تا اولین پاسخ اپراتور" footer={<Delta cur={cur?.avg_first_response_sec} prev={prev?.avg_first_response_sec} good="down" />} />
          <KpiCard loading={loading && !data} label="نرخ پاسخ‌دهی" value={fmtPercent(cur?.answered_rate)} icon={<CheckCircleOutlined />} color="#059669"
            hint="درصد گفتگوهایی که حداقل یک پاسخ اپراتور گرفته‌اند" footer={<Delta cur={cur?.answered_rate} prev={prev?.answered_rate} good="up" />} />
          <KpiCard loading={loading && !data} label="گفتگوی بسته‌شده" value={fmtCount(cur?.closed)} icon={<CheckCircleOutlined />} color="#16a34a"
            hint="گفتگوهایی که در بازه بسته شده‌اند" footer={<Delta cur={cur?.closed} prev={prev?.closed} good="up" />} />
          <KpiCard loading={loading && !data} label="میانگین زمان حل" value={fmtDuration(cur?.avg_resolution_sec)} icon={<ClockCircleOutlined />} color="#7c3aed"
            hint="میانگین زمان از شروع تا بسته‌شدن گفتگو" footer={<Delta cur={cur?.avg_resolution_sec} prev={prev?.avg_resolution_sec} good="down" />} />
          <KpiCard loading={loading && !data} label="پیام دریافتی" value={fmtCount(cur?.messages_in)} icon={<ArrowDownOutlined />} color="#2563eb"
            hint="پیام‌های ارسالی مشتریان" footer={<Delta cur={cur?.messages_in} prev={prev?.messages_in} good="neutral" />} />
          <KpiCard loading={loading && !data} label="پیام ارسالی" value={fmtCount(cur?.messages_out)} icon={<ArrowUpOutlined />} color="#0d9488"
            hint="پاسخ‌های اپراتورها"
            footer={
              cur?.messages_failed > 0 ? (
                <span className="text-xs text-red-600 custom-font">{fmtCount(cur.messages_failed)} پیام ناموفق</span>
              ) : (
                <Delta cur={cur?.messages_out} prev={prev?.messages_out} good="neutral" />
              )
            } />
        </div>

        {!loading && data && !hasAny && (
          <Card className="shadow-sm border-0"><Empty description={<span className="custom-font">هنوز گفتگویی در این بازه ثبت نشده است</span>} /></Card>
        )}

        {/* روند */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Section title="روند گفتگوها و پیام‌ها" className="xl:col-span-2">
            {loading && !data ? <Skeleton active /> : (
              <div dir="ltr" style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="lcConv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="lcIn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d97706" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#d97706" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af33" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontFamily: 'CustomFont', fontSize: 12 }} />
                    <Area type="monotone" dataKey="conversations" name="گفتگوی جدید" stroke="#2563eb" fill="url(#lcConv)" strokeWidth={2} />
                    <Area type="monotone" dataKey="messages_in" name="پیام دریافتی" stroke="#d97706" fill="url(#lcIn)" strokeWidth={2} />
                    <Area type="monotone" dataKey="closed" name="بسته‌شده" stroke="#16a34a" fillOpacity={0} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title="سهم منابع ورودی">
            {loading && !data ? <Skeleton active /> : sourceData.length === 0 ? <ChartEmpty /> : (
              <div dir="ltr" style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sourceData} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {sourceData.map((s) => <Cell key={s.id} fill={s.fill} />)}
                    </Pie>
                    <ChartTooltip contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontFamily: 'CustomFont', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
        </div>

        {/* الگوی زمانی */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Section title="پیام‌های ورودی بر اساس ساعت شبانه‌روز">
            {loading && !data ? <Skeleton active /> : (
              <div dir="ltr" style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hours} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af33" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(v) => [v, 'پیام']} labelFormatter={(l) => `ساعت ${l}:00`} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {hours.map((h) => <Cell key={h.hour} fill={peakHour && h.hour === peakHour.hour ? '#7c3aed' : '#a78bfa'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title="گفتگوهای جدید بر اساس روز هفته">
            {loading && !data ? <Skeleton active /> : (
              <div dir="ltr" style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekdays} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af33" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(v) => [v, 'گفتگو']} />
                    <Bar dataKey="count" fill="#0d9488" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
        </div>

        {/* بخش‌ها */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Section title="گفتگو به تفکیک بخش">
            {loading && !data ? <Skeleton active /> : (data?.by_department ?? []).length === 0 ? <ChartEmpty /> : (
              <div className="space-y-3">
                {data.by_department.map((d) => (
                  <div key={d.id ?? 'none'}>
                    <div className="flex items-center justify-between text-xs custom-font mb-1">
                      <span className="text-gray-700 dark:text-gray-200">{d.name}</span>
                      <span className="text-gray-500">{fmtCount(d.count)} گفتگو · {fmtCount(d.open)} باز</span>
                    </div>
                    <Progress percent={deptTotal ? Math.round((d.count / deptTotal) * 100) : 0} size="small" strokeColor="#0d9488" />
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="پرتکرارترین زیربخش‌ها">
            {loading && !data ? <Skeleton active /> : (data?.by_sub_department ?? []).length === 0 ? <ChartEmpty /> : (
              <div dir="ltr" style={{ height: Math.max(200, data.by_sub_department.length * 34) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_sub_department} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                    <ChartTooltip contentStyle={chartTooltipStyle} formatter={(v) => [v, 'گفتگو']} />
                    <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title="سایت‌های مبدأ (ویجت)">
            {loading && !data ? <Skeleton active /> : (data?.top_sites ?? []).length === 0 ? <ChartEmpty /> : (
              <div className="space-y-2">
                {data.top_sites.map((s) => (
                  <div key={s.site} className="flex items-center justify-between">
                    <Tag color="cyan" style={{ direction: 'ltr', marginInlineEnd: 0 }}>{s.site}</Tag>
                    <span className="text-sm font-semibold custom-font">{fmtCount(s.count)}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* اپراتورها */}
        {(data?.operators ?? []).length > 0 && (
          <Section title="عملکرد اپراتورها">
            <Table
              size="small"
              rowKey="id"
              columns={operatorColumns}
              dataSource={data.operators}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Section>
        )}

        {/* نیازمند توجه */}
        <Section
          title="گفتگوهای نیازمند توجه"
          extra={<Button type="link" className="custom-font" onClick={() => navigate('/live-chat')}>صندوق گفتگو</Button>}
        >
          {(data?.needs_attention ?? []).length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="custom-font">همه چیز تحت کنترل است</span>} />
          ) : (
            <Table size="small" rowKey="id" columns={attentionColumns} dataSource={data.needs_attention} pagination={false} scroll={{ x: 'max-content' }} />
          )}
        </Section>
      </div>
    </MainLayout>
  );
};

export default LiveChatDashboard;
